import { Signal, useSignal } from '@preact/signals';

interface Position {
  x: number;
  y: number;
}

const STORAGE_PREFIX = 'panel-position-';

/**
 * Load panel position from localStorage
 */
export function loadPosition(panelId: string): Position | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${panelId}`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Save panel position to localStorage
 */
export function savePosition(panelId: string, position: Position): void {
  try {
    // Persist integer pixel positions to avoid fractional values which can
    // cause flakiness in tests and inconsistent styling across devices.
    localStorage.setItem(
      `${STORAGE_PREFIX}${panelId}`,
      JSON.stringify({ x: Math.round(position.x), y: Math.round(position.y) })
    );
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for managing panel dragging behavior
 */
export function usePanelDrag(
  panelRef: { current: HTMLDivElement | null },
  isDragging: Signal<boolean>,
  position: Signal<Position | null>,
  panelId?: string
) {
  const dragStart = useSignal<Position | null>(null);

  const handleMouseDown = (e: MouseEvent) => {
    // Only drag if clicking on the header (not buttons)
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }

    isDragging.value = true;
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      dragStart.value = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !dragStart.value) return;

    const newPosition = {
      x: Math.round(e.clientX - dragStart.value.x),
      y: Math.round(e.clientY - dragStart.value.y),
    };

    // Keep panel within viewport bounds
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      newPosition.x = Math.max(0, Math.min(newPosition.x, window.innerWidth - rect.width));
      newPosition.y = Math.max(0, Math.min(newPosition.y, window.innerHeight - rect.height));
    }

    position.value = newPosition;
  };

  const handleMouseUp = () => {
    if (isDragging.value && position.value && panelId) {
      savePosition(panelId, position.value);
    }
    isDragging.value = false;
    dragStart.value = null;
  };

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
